import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Grid,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Tooltip,
} from "@mzc-pdc/ui";
import { IconAdd, IconAddCircle, IconRefresh, IconSearch } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { NoSearchResultsIcon } from "@features/no-search-results-icon";
import { useInView } from "react-intersection-observer";
import AutocompleteTableListToolCollapse from "@features/autocomplete-table/autocomplete-table-list-tool-collapse";
import { useSelector } from "react-redux";
import i18n from "@lib/i18n";

export const AutocompleteFixedTable = ({
    domain,
    onSubmit,
    columns,
    skeletonColumns,
    placeholder,
    options,
    isPending,
    getValueByColumn,
    onFetchData,
    selectedItems = [],
    viewMore,
    onDebounceSearch,
    hideCheckbox,
}) => {
    let textFieldRef = useRef();
    const { t } = useTranslation();

    const [searchValue, setSearchValue] = useState({ searchedValue: "", searchParams: null });

    const [targetRef, inView] = useInView({
        threshold: 0,
    });
    const DEFAULT_SKELETON_CNT = 20;

    const getSkeletonCardLength = useCallback(() => {
        if (!options || options?.length === 0) return DEFAULT_SKELETON_CNT;
        return 3;
    }, [isPending]);

    const isChecked = useCallback(
        (option) => {
            return selectedItems.find((x) => x.id === option.id) !== undefined;
        },
        [selectedItems],
    );

    const onHandleCheckClick = (checked, option) => {
        let newChecked = [];

        if (hideCheckbox) {
            onSubmit([option]);
            return;
        }

        if (checked) {
            newChecked = [...selectedItems];
            newChecked.push(option);
        } else {
            newChecked = selectedItems?.filter((item) => item.id !== option.id);
        }
        onSubmit(newChecked);
    };

    const isCollapsed = useMemo(() => selectedItems.length > 0, [selectedItems]);

    const onChangeNumSelected = (checkAll) => {
        if (checkAll) {
            onSubmit(options);
        } else {
            onSubmit([]);
        }
    };

    const handleClearCheckAll = () => {
        onChangeNumSelected(false);
    };

    const handleChangeCheckAll = () => {
        onChangeNumSelected(options?.length !== selectedItems.length);
    };

    const getListToolsButtons = useCallback(
        (isResponsive) => {
            return [
                <TooltipButton
                    key={"add-to-all-button"}
                    circled
                    size={"small"}
                    title={t("common::label::Add to all", "Add to all")}
                    onClickButton={() => onSubmit(options)}
                    buttonIcon={<IconAddCircle size={16} htmlColor={theme.palette.secondary.contrastText} />}
                    isEndIcon={true}
                    isDivided={false}
                    buttonText={t("common::label::Add to all", "Add to all")}
                    isResponsive={isResponsive}
                />,
            ];
        },
        [selectedItems],
    );

    const onHandleChangeSearch = (value) => {
        if (value !== searchValue.searchedValue) {
            setSearchValue({ ...searchValue, searchedValue: value });
        }
    };

    const onChangeSearchParams = (value) => {
        setSearchValue({
            ...searchValue,
            searchParams: {
                ...value,
                searchKeyword: searchValue.searchParams?.searchKeyword,
            },
        });
    };

    useEffect(() => {
        const searchedValue = searchValue.searchedValue;
        if (searchedValue === null) return;
        const debounce = setTimeout(
            () => {
                return setSearchValue({
                    ...searchValue,
                    searchParams: { ...searchValue.searchParams, searchKeyword: searchedValue },
                });
            },
            // 최초 조회(searchParams 미설정)는 디바운스 없이 즉시 실행 (스켈레톤 지연 방지), 이후 타이핑 검색만 디바운스
            searchValue.searchParams === null ? 0 : 300,
        );
        return () => clearTimeout(debounce);
    }, [searchValue.searchedValue]);

    useEffect(() => {
        if (!onDebounceSearch) return;
        const { searchParams } = searchValue;
        if (!searchParams) return;
        onDebounceSearch(searchParams);
    }, [searchValue.searchParams]);

    useEffect(() => {
        if (isPending) return;
        if (inView) {
            onFetchData(searchValue.searchParams);
        }
    }, [inView]);

    return (
        <Grid container flexDirection={"column"} height={"100%"}>
            <Grid item>
                <TextField
                    ref={textFieldRef}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconSearch size={12} />
                            </InputAdornment>
                        ),
                    }}
                    fullWidth
                    type="search"
                    variant="outlined"
                    placeholder={placeholder}
                    autoComplete={"off"}
                    onChange={(e) => {
                        e.stopPropagation();
                        const replacedString = e.target.value.replace(/[\n\s\r\t]*/g, "");
                        if (onDebounceSearch) onHandleChangeSearch(replacedString);
                    }}
                />
            </Grid>
            <Grid item flex={1} xs sx={{ display: `flex`, flexDirection: `column`, overflow: `hidden`, width: "100%" }}>
                <AutocompleteTableListToolCollapse
                    type={domain}
                    isCollapsed={isCollapsed && !hideCheckbox}
                    handleClearCheckAll={handleClearCheckAll}
                    handleChangeCheckAll={handleChangeCheckAll}
                    numSelected={selectedItems?.length}
                    pageCount={options?.length}
                    buttons={getListToolsButtons}
                    hideCheckbox={hideCheckbox}
                    onChangeSearchParams={onChangeSearchParams}
                />
                <TableContainer sx={{ overflowY: "auto", height: "100%" }}>
                    {!isPending && options?.length === 0 && <NoSearchResultsIcon />}
                    <Table stickyHeader shadowHeader borderHeader sx={{ tableLayout: "fixed" }}>
                        <TableBody>
                            {options?.length > 0 &&
                                options?.map((option, index) => (
                                    <TableRow
                                        key={`autocomplete-table-row-${index}`}
                                        id={option.id}
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onHandleCheckClick(!isChecked(option), option);
                                        }}
                                        selected={selectedItems.find((item) => item.id === option.id) !== undefined}
                                    >
                                        {!hideCheckbox && (
                                            <TableCell sx={{ px: 0, width: "24px" }}>
                                                <Checkbox
                                                    color={"greySecondary"}
                                                    checked={isChecked(option)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onHandleCheckClick(!isChecked(option), option);
                                                    }}
                                                    sx={{ pr: 0 }}
                                                />
                                            </TableCell>
                                        )}
                                        {columns
                                            .filter((item) => item.useInAutocomplete)
                                            .map((column, index) => (
                                                <TableCell
                                                    key={`autocomplete-table-cell-${index}`}
                                                    sx={{
                                                        ...column.style?.custom,
                                                    }}
                                                >
                                                    {getValueByColumn(column.id, option, {
                                                        ...column,
                                                        checkbox: !hideCheckbox,
                                                    })}
                                                </TableCell>
                                            ))}
                                    </TableRow>
                                ))}
                            {(isPending || !options) &&
                                new Array(getSkeletonCardLength())
                                    .fill("")
                                    .map((_, i) => (
                                        <TableRow key={`autocomplete-table-skeleton-${i}`}>{skeletonColumns}</TableRow>
                                    ))}
                            {viewMore && <div style={{ height: "1px" }} ref={targetRef} id={"addResourcesLoadMore"} />}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Grid
                    container
                    sx={{
                        flexBasis: theme.spacing(5),
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        mt: theme.spacing(2.5),
                    }}
                >
                    <Tooltip title={i18n.t(`common::label::Refresh`, `Refresh`)}>
                        <IconButton
                            circled
                            variant={"text"}
                            color={"inherit"}
                            size={`medium`}
                            onClick={() => {
                                onDebounceSearch(searchValue.searchParams);
                            }}
                            sx={{ mr: theme.spacing(1) }}
                        >
                            <IconRefresh size={16} />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AutocompleteFixedTable;
